import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { isValid, parse } from "date-fns"
import { HighlightCellText } from "@/components/highlight-cell-text"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { useDetectSticky } from "@/hooks/useDetectSticky"
import { DateOnly, DateOnlyFormat } from "@/lib/date-only"
import { getSlotLabel } from "@/lib/slot"
import {
  ShiftType,
  useAgentPlanningsQuery,
} from "@/modules/admin/agent-plannings/services/__generated__/useAgentPlanningsQuery.graphql"
import { agentPlanningsQuery } from "@/modules/admin/agent-plannings/services/useAgentPlanningsQuery"

type AgentShift = {
  userName: string
  date: string
  hour: string
  slotId: number
  shiftTypes: ReadonlyArray<ShiftType>
}

type AgentPlanningsTableProps = {
  queryRef: PreloadedQuery<useAgentPlanningsQuery>
}

type ColumnProps<AgentShift> = { header: string; cell: (props: { data: AgentShift }) => React.ReactNode }

const columns: ColumnProps<AgentShift>[] = [
  {
    header: "Username",
    cell: ({ data }) => <HighlightCellText text={data.userName} title={data.userName} />,
  },
  {
    header: "Date",
    cell: ({ data }) => <HighlightCellText text={data.date} title={data.date} />,
  },
  {
    header: "Hour",
    cell: ({ data }) => <HighlightCellText text={data.hour} title={data.hour} />,
  },
  {
    header: "Slot",
    cell: ({ data }) => <HighlightCellText text={data.slotId.toString()} title={data.slotId.toString()} />,
  },
  {
    header: "Shifts",
    cell: ({ data }) => {
      const shiftTypesText = data.shiftTypes.join(", ")
      return <HighlightCellText text={shiftTypesText} title={shiftTypesText} />
    },
  },
]

export const AgentPlanningsTable: React.FC<AgentPlanningsTableProps> = ({ queryRef }) => {
  const { t } = useTranslation()

  const onIntersectionTriggered = (isSticky: boolean) => {
    if (!ref.current) return

    const element = ref.current.querySelector("[data-testid='table-header-container']")
    if (element) {
      element.classList.toggle("table-header-shadow", isSticky)
    }
  }
  const ref = useDetectSticky({ onIntersectionTriggered })

  const { doctors } = usePreloadedQuery<useAgentPlanningsQuery>(agentPlanningsQuery, queryRef)

  const results: AgentShift[] = useMemo(() => {
    const agentShifts: AgentShift[] = []
    const today = new DateOnly(new Date())
    const validDates = [today.toString(), today.addDays(1).toString()]

    for (const item of doctors?.edges || []) {
      for (const shift of item.node.shifts) {
        const rowDate = parse(shift.date, DateOnlyFormat, new Date())
        if (isValid(rowDate)) {
          if (validDates.includes(shift.date)) {
            agentShifts.push({
              userName: item.node.userName,
              date: shift.date,
              hour: getSlotLabel(shift.slotId),
              slotId: shift.slotId,
              shiftTypes: [...shift.shiftTypes].sort(),
            })
          } else {
            // Shift data is sorted by date ASC, beginning from today.
            // So we can break the loop if we reach the day after tomorow.
            break
          }
        }
      }
    }

    return agentShifts
  }, [doctors?.edges])

  const getRowKey = (item: AgentShift) => `${item.userName}-${item.date}-${item.slotId}`

  const AgentPlanningRow = (item: AgentShift) => {
    const rowKey = getRowKey(item)
    return (
      <div
        key={rowKey}
        data-testid="table-row"
        className="border-border text-on-surface hover:bg-surface-hovered relative col-span-full grid h-10 cursor-pointer items-center justify-center gap-4 px-4 py-2 text-sm"
        role="button"
        style={{
          gridTemplateColumns: "minmax(0,5fr) minmax(0,3fr) minmax(0,3fr) minmax(0,3fr) minmax(0,3fr) 80px",
        }}>
        {columns.map((column) => (
          <span key={`${rowKey}-${column.header}`} className="truncate" data-testid="table-cell">
            {column.cell({ data: item })}
          </span>
        ))}
      </div>
    )
  }

  return (
    <ScrollWrapper className="pb-8">
      <div className="px-8">
        <div className="relative mx-auto grid w-full overflow-x-clip">
          <div className="bg-background-subdued sticky -top-px z-10 pt-px" ref={ref}>
            {results.length > 0 ? (
              <div
                data-testid="table-header-container"
                className="border-border text-on-surface-subdued bg-surface grid gap-4 rounded-t-xl border px-4 py-2 text-xs"
                style={{
                  gridTemplateColumns: "minmax(0,5fr) minmax(0,3fr) minmax(0,3fr) minmax(0,3fr) minmax(0,3fr) 80px",
                }}>
                {columns.map((column) => (
                  <div key={column.header} className="flex items-center" data-testid="table-header">
                    <span className="truncate">{t(column.header)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="animate-fade-in-bottom grid h-full content-center items-center justify-center gap-4 p-3 text-center">
                <div className="grid gap-1">
                  <h2 className="text-on-surface text-xl font-bold">{t("common.dataNotFound")}</h2>
                </div>
              </div>
            )}
          </div>
          <div
            data-testid="table-row-container"
            className="border-border bg-surface col-span-full grid divide-y overflow-hidden rounded-b-xl border-x border-b text-sm">
            {results.map((item) => AgentPlanningRow(item))}
          </div>
        </div>
      </div>
    </ScrollWrapper>
  )
}
